/** @type {Record<string, (t: number) => number>} */
const easingMap = {
  linear: (t) => t,
  easeOut: (t) => 1 - Math.pow(1 - t, 3),
  easeInOut: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
};

/**
 * @param {number} value
 * @param {number} decimals
 * @returns {string}
 */
function formatNumber(value, decimals) {
  if (decimals > 0) {
    return Math.max(0, value).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  return Math.max(0, Math.floor(value)).toLocaleString();
}

/**
 * @param {number} value
 * @param {number} decimals
 * @returns {string}
 */
function formatFinal(value, decimals) {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Measures the final text width and locks the counter element to that width,
 * so the layout doesn't shift as the number animates up.
 *
 * @param {Element} counterEl
 */
function lockCounterWidth(counterEl) {
  const raw = (counterEl.getAttribute('data-target-number') || '').trim();
  if (!raw) return;

  const computedStyle = window.getComputedStyle(counterEl);
  const measureEl = document.createElement('span');

  Object.assign(measureEl.style, {
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    whiteSpace: 'pre',
    fontFamily: computedStyle.fontFamily,
    fontSize: computedStyle.fontSize,
    fontWeight: computedStyle.fontWeight,
    fontStyle: computedStyle.fontStyle,
    letterSpacing: computedStyle.letterSpacing,
    lineHeight: computedStyle.lineHeight,
    textTransform: computedStyle.textTransform,
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum" 1, "lnum" 1',
  });

  measureEl.textContent = raw;
  document.body.appendChild(measureEl);
  const measuredWidth = Math.ceil(measureEl.getBoundingClientRect().width);
  measureEl.remove();

  if (measuredWidth > 0) {
    /** @type {HTMLElement} */ (counterEl).style.minWidth = `${measuredWidth}px`;
  }
}

/**
 * Runs the count-up animation on a single counter element.
 *
 * @param {Element} counterEl
 * @param {number} durationMs
 * @param {(t: number) => number} easingFn
 */
function animateCounter(counterEl, durationMs, easingFn) {
  const raw = (counterEl.getAttribute('data-target-number') || '').trim();
  if (!raw) return;

  const numberMatches = raw.match(/\d[\d,]*(?:\.\d+)?/g);
  if (!numberMatches?.length) return;

  const numbers = numberMatches.map((match) => {
    const normalized = match.replace(/,/g, '');
    const targetValue = Number.parseFloat(normalized);
    const decimalPart = normalized.split('.')[1];
    return { targetValue, decimals: decimalPart ? decimalPart.length : 0 };
  });

  if (numbers.some((n) => !Number.isFinite(n.targetValue))) return;

  const templateParts = raw.split(/(\d[\d,]*(?:\.\d+)?)/g);
  /** @type {number | null} */
  let startTime = null;
  counterEl.textContent = raw.replace(/\d[\d,]*(?:\.\d+)?/g, '0');

  /** @param {number} timestamp */
  const step = (timestamp) => {
    if (startTime === null) startTime = timestamp;
    const progress = Math.min((timestamp - startTime) / durationMs, 1);
    const easedProgress = easingFn(progress);
    let numberIndex = 0;

    counterEl.textContent = templateParts
      .map((part, partIndex) => {
        if (partIndex % 2 === 0) return part;
        const numberConfig = numbers[numberIndex++];
        if (!numberConfig) return part;
        return progress < 1
          ? formatNumber(numberConfig.targetValue * easedProgress, numberConfig.decimals)
          : formatFinal(numberConfig.targetValue, numberConfig.decimals);
      })
      .join('');

    if (progress < 1) requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
}

/**
 * Wires up the intersection-observer–driven counter animation for a single section element.
 *
 * @param {Element} sectionEl
 */
function initProductDataSection(sectionEl) {
  if (!(sectionEl instanceof HTMLElement)) return;
  if (sectionEl.dataset.productDataInit) return;
  sectionEl.dataset.productDataInit = 'true';

  const counters = Array.from(sectionEl.querySelectorAll('[data-product-data-number]'));
  if (!counters.length) return;

  const durationMs = Number(sectionEl.dataset.animationDurationMs || 1000);
  const staggerMs = Number(sectionEl.dataset.animationStaggerMs || 0);
  const easingKey = sectionEl.dataset.animationEasing || 'easeOut';
  const easingFn = (/** @type {number} */ t) => {
    const baseEasing = easingMap[easingKey] ?? easingMap.easeOut ?? easingMap.linear;
    return baseEasing ? baseEasing(t) : t;
  };

  counters.forEach(lockCounterWidth);

  let hasAnimated = false;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || hasAnimated) return;
        hasAnimated = true;

        counters.forEach((counterEl, index) => {
          const raw = (counterEl.getAttribute('data-target-number') || '').trim();
          if (raw) counterEl.textContent = raw.replace(/\d[\d,]*(?:\.\d+)?/g, '0');
          setTimeout(() => animateCounter(counterEl, durationMs, easingFn), staggerMs * index);
        });

        observer.disconnect();
      });
    },
    { threshold: 0.35 }
  );

  observer.observe(sectionEl);
}

// ─── Bootstrap ────────────────────────────────────────────────────────────────

document.querySelectorAll('[data-product-data-section]').forEach(initProductDataSection);

document.addEventListener('shopify:section:load', (event) => {
  const root = /** @type {HTMLElement | null} */ (event.target);
  if (!root) return;
  if (root.matches?.('[data-product-data-section]')) initProductDataSection(root);
  root.querySelectorAll?.('[data-product-data-section]').forEach(initProductDataSection);
});
